/**
 * ZTNA Admin Dashboard — read-only Lote 6 (TASK-9006).
 *
 * Container que orquestra:
 *  - 5 useQuery hooks (kpis, apps, access stats 24h, cert expiry 90d,
 *    onboarding history 10).
 *  - State local de filtros (search/envs/statuses) com aplicação client-side
 *    sobre a lista de apps já carregada (sem refetch).
 *  - State drawerApp para AppDrawer.
 *  - Header com botão "+ Cadastrar app" desabilitado (Lote 7 wizard).
 *
 * Refetch: TanStack Query com staleTime customizado por endpoint para
 * reduzir tráfego (apps refresh 30s, kpis 60s, history 60s).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Group,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconPlus, IconAlertTriangle, IconHeartbeat, IconLayoutDashboard } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useFeatureFlag } from '@/hooks/useFeatureFlags'
import { ztnaApi } from '../api'
import {
  type AppHealthSummary,
  deriveHealthBucket,
  type FilterState,
} from '../types'
import { JobBanner } from '../JobBanner/JobBanner'
import { useZtnaJobRealtime } from '../useZtnaJobRealtime'
import { AppCardGrid } from './AppCardGrid'
import { AppDrawer } from './AppDrawer'
import { CertExpiryWatchlist } from './CertExpiryWatchlist'
import { ChartsRow } from './ChartsRow'
import { FiltersToolbar } from './FiltersToolbar'
import { HealthTab } from './HealthTab'
import { KpiCards } from './KpiCards'
import { OnboardingHistoryTable } from './OnboardingHistoryTable'

const STALE_30S = 30_000
const STALE_60S = 60_000

const DEFAULT_FILTERS: FilterState = {
  search: '',
  envs: [],
  statuses: [],
}

export function Dashboard() {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS)
  const [drawerApp, setDrawerApp] = useState<AppHealthSummary | null>(null)
  const wizardEnabled = useFeatureFlag('ztna_wizard_enabled')

  // Realtime SSE do onboarding: invalida o grid ao concluir (sem F5) e destaca
  // o card recém-provisionado. Sempre montado enquanto o dashboard vive.
  const { finished, clearFinished } = useZtnaJobRealtime()
  const [highlightSlug, setHighlightSlug] = useState<string | null>(null)
  useEffect(() => {
    if (finished?.status === 'success' && finished.slug) {
      setHighlightSlug(finished.slug)
      const t = setTimeout(() => setHighlightSlug(null), 6000)
      return () => clearTimeout(t)
    }
    return undefined
  }, [finished])

  const kpisQuery = useQuery({
    queryKey: ['ztna', 'kpis'],
    queryFn: () => ztnaApi.getKpis(),
    staleTime: STALE_60S,
  })

  const appsQuery = useQuery({
    queryKey: ['ztna', 'apps', { includeDisabled: !!filters.includeDisabled }],
    queryFn: () =>
      ztnaApi.listApps({ includeDisabled: !!filters.includeDisabled }),
    staleTime: STALE_30S,
  })

  const historyQuery = useQuery({
    queryKey: ['ztna', 'onboarding-history'],
    queryFn: () => ztnaApi.getOnboardingHistory(10),
    staleTime: STALE_60S,
  })

  const certExpiryQuery = useQuery({
    queryKey: ['ztna', 'cert-expiry'],
    queryFn: () => ztnaApi.getCertExpiry(90),
    staleTime: STALE_60S,
  })

  const accessStatsQuery = useQuery({
    queryKey: ['ztna', 'access-stats', '24h'],
    queryFn: () => ztnaApi.getAccessStats('24h'),
    staleTime: STALE_60S,
  })

  const apps = appsQuery.data ?? []

  const filteredApps = useMemo(() => {
    const search = filters.search.trim().toLowerCase()
    return apps.filter((app) => {
      if (search) {
        const hay = [
          app.slug,
          app.hostname,
          String(app.tenant_id),
          app.environment,
        ]
          .join(' ')
          .toLowerCase()
        if (!hay.includes(search)) return false
      }
      if (filters.envs.length > 0 && !filters.envs.includes(app.environment.toUpperCase())) {
        return false
      }
      if (filters.statuses.length > 0) {
        const bucket = deriveHealthBucket(app)
        if (!filters.statuses.includes(bucket)) return false
      }
      return true
    })
  }, [apps, filters])

  // top_apps vem do /dashboard/access-stats (Bug fix 2026-05-10): inclui
  // tenant_id + environment pra desambiguar slugs duplicados (ex: 3 apps
  // com slug 'portal' em SafeCheck DEV/HML e OnMidia QA).
  const topApps = accessStatsQuery.data?.top_apps ?? []

  const denyReasons = accessStatsQuery.data?.deny_reasons ?? []
  const certExpiryItems = certExpiryQuery.data ?? []

  const anyError =
    kpisQuery.error || appsQuery.error || historyQuery.error ||
    certExpiryQuery.error || accessStatsQuery.error

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2} c="var(--ai-brand-azul)">
            ZTNA Admin
          </Title>
          <Text size="sm" c="dimmed">
            Visão geral das aplicações ZTNA, health, acessos e certificados.
          </Text>
        </div>
        {wizardEnabled ? (
          <Button
            component={Link}
            to="/admin/ztna/onboard"
            leftSection={<IconPlus size={16} />}
            variant="filled"
            color="inovareTeal"
          >
            Cadastrar app
          </Button>
        ) : (
          <Tooltip
            label="Wizard não habilitado (ZTNA_WIZARD_ENABLED=false)"
            position="left"
            withArrow
          >
            <span>
              <Button
                leftSection={<IconPlus size={16} />}
                disabled
                variant="filled"
                color="inovareTeal"
              >
                Cadastrar app
              </Button>
            </span>
          </Tooltip>
        )}
      </Group>

      <JobBanner finished={finished} onDismiss={clearFinished} />

      <Tabs defaultValue="overview" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconLayoutDashboard size={14} />}>
            Visão Geral
          </Tabs.Tab>
          <Tabs.Tab value="health" leftSection={<IconHeartbeat size={14} />}>
            Saúde
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <Stack gap="md">
            {anyError ? (
              <Alert
                color="red"
                variant="light"
                icon={<IconAlertTriangle size={16} />}
                title="Falha ao carregar dados"
              >
                Um ou mais endpoints do dashboard falharam. Verifique permissões
                (ztna:onboard:provision) ou tente recarregar.
              </Alert>
            ) : null}

            <FiltersToolbar value={filters} onChange={setFilters} />

            <KpiCards
              data={kpisQuery.data}
              isLoading={kpisQuery.isLoading}
              onFilterClick={(kpi) => {
                // Click no card "Suspensas" liga o filtro pra mostrar
                // suspensas no grid. Outros KPIs ainda sao no-op.
                if (kpi === 'disabled') {
                  setFilters((f) => ({ ...f, includeDisabled: true }))
                }
              }}
            />

            <ChartsRow
              topApps={topApps}
              denyReasons={denyReasons}
              isLoading={accessStatsQuery.isLoading}
            />

            <AppCardGrid apps={filteredApps} onSelectApp={setDrawerApp} highlightSlug={highlightSlug} />

            <CertExpiryWatchlist items={certExpiryItems} />

            <OnboardingHistoryTable
              rows={historyQuery.data}
              isLoading={historyQuery.isLoading}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="health" pt="md">
          <HealthTab />
        </Tabs.Panel>
      </Tabs>

      <AppDrawer app={drawerApp} onClose={() => setDrawerApp(null)} />
    </Stack>
  )
}

export default Dashboard
